import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, SearchX, Sparkles, ArrowRight } from "lucide-react";
import UnifiedSearchBar from "@/components/UnifiedSearchBar";
import FilterChips from "@/components/FilterChips";
import PersonCard from "@/components/PersonCard";
import PostProblemModal from "@/components/PostProblemModal";
import AIMatchDialog from "@/components/AIMatchDialog";
import { PEOPLE, CATEGORIES, AI_MODELS, AI_SOFTWARE_TOOLS } from "@/data/mockPeople";
import { fuzzySearch, filterByProblem } from "@/lib/search";
import { api, useAuth, goToLogin, signInLabel } from "@/contexts/AuthContext";
import { realUserToPerson } from "@/lib/adapters";

export default function DirectoryPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeTools, setActiveTools] = useState([]);
  const [activeSoftwareTools, setActiveSoftwareTools] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [aiMatchOpen, setAiMatchOpen] = useState(false);
  const [realPeople, setRealPeople] = useState([]);

  useEffect(() => {
    api
      .get("/users")
      .then((r) => setRealPeople((r.data || []).map(realUserToPerson)))
      .catch(() => setRealPeople([]));
  }, []);

  const allPeople = useMemo(
    // Real users only in the Professional Directory now that we have live
    // profiles. Demo profiles still surface on Ask a Question + Answered
    // Questions so those pages don't look empty.
    () => [...realPeople],
    [realPeople]
  );

  // Merge predefined lists with anything real users have actually submitted so
  // custom entries become clickable filter chips (not just free-text matches).
  const mergedCategories = useMemo(() => {
    const set = new Set(CATEGORIES);
    for (const p of allPeople) {
      for (const c of p.categories || []) if (c) set.add(c);
      for (const pr of p.problems || []) {
        for (const c of pr.categories || []) if (c) set.add(c);
      }
    }
    return [...set];
  }, [allPeople]);

  const mergedTools = useMemo(() => {
    const set = new Set(AI_MODELS);
    for (const p of allPeople) {
      for (const t of p.tools || []) if (t?.name) set.add(t.name);
      for (const pr of p.problems || []) {
        for (const t of pr.tools || []) if (t) set.add(t);
      }
    }
    return [...set];
  }, [allPeople]);

  const mergedSoftwareTools = useMemo(() => {
    const set = new Set(AI_SOFTWARE_TOOLS);
    for (const p of allPeople) {
      for (const t of p.software_tools || []) if (t) set.add(t);
    }
    return [...set];
  }, [allPeople]);

  const toggle = (list, setList) => (item) => {
    setList(
      list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
    );
  };

  // Add a user-typed custom value directly into the active filter list.
  const submitCustom = (list, setList) => (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (list.includes(trimmed)) return;
    setList([...list, trimmed]);
  };

  const filtered = useMemo(() => {
    const searched = fuzzySearch(allPeople, query);
    return filterByProblem(
      searched,
      activeCategories,
      activeTools,
      activeSoftwareTools
    );
  }, [query, activeCategories, activeTools, activeSoftwareTools, allPeople]);

  const resetAll = () => {
    setQuery("");
    setActiveCategories([]);
    setActiveTools([]);
    setActiveSoftwareTools([]);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 lg:pt-14 pb-16">
      {/* Marketing pitch — only for logged-out visitors, converts first-time
          landings by explaining what Oiiister is before dropping them into the
          directory. Existing members skip this. */}
      {!user && (
        <section
          data-testid="marketing-strip"
          className="mb-10 rounded-3xl bg-gradient-to-br from-[#D6FFEB] via-[#EBFFF5] to-white ring-1 ring-[#D6FFEB] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-lg sm:text-xl text-[#111827] leading-snug">
              A professional network for{" "}
              <span className="text-[#110A73]">real-world AI work</span>, where
              every contribution is proof of expertise.
            </p>
            <p className="mt-1.5 text-sm sm:text-base text-[#4B5563] leading-relaxed">
              Join Oiiister — it's free and always will be.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/about"
              data-testid="marketing-learn-more"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white ring-1 ring-[#E5E7EB] hover:ring-[#3FFFB6] text-sm font-semibold text-[#111827] hover:text-[#3FFFB6] transition-colors"
            >
              Learn more
              <ArrowRight size={14} />
            </Link>
            <button
              type="button"
              onClick={() => goToLogin(pathname)}
              data-testid="marketing-signin"
              className="inline-flex items-center h-10 px-4 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
            >
              {signInLabel()}
            </button>
          </div>
        </section>
      )}

      {/* Hero */}
      <section className="max-w-3xl">
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111827]">
          Get direct access to AI experts —{" "}
          <span className="text-[#110A73]">then become one</span>.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-2xl">
          <strong className="font-semibold text-[#111827]">
            Join the Oiiister community — it's free and always will be.
          </strong>
          {" Find the right expert to help solve a sticky AI build, collaborate on your next project, or recruit as your next hire."}
        </p>
      </section>

      {/* Search + Filters */}
      <section className="mt-12">
        <UnifiedSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search a problem, tool, or outcome…"
        />
        <button
          onClick={() => setAiMatchOpen(true)}
          data-testid="ai-match-cta"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#111827] hover:text-[#2DE59F] transition-colors group"
        >
          <Sparkles size={14} className="group-hover:rotate-12 transition-transform" />
          Or describe your problem in plain English and let AI find your match
          <span aria-hidden>→</span>
        </button>
        <div className="mt-6">
          <FilterChips
            categories={mergedCategories}
            tools={mergedTools}
            softwareTools={mergedSoftwareTools}
            activeCategories={activeCategories}
            activeTools={activeTools}
            activeSoftwareTools={activeSoftwareTools}
            onToggleCategory={toggle(activeCategories, setActiveCategories)}
            onToggleTool={toggle(activeTools, setActiveTools)}
            onToggleSoftwareTool={toggle(activeSoftwareTools, setActiveSoftwareTools)}
            onSubmitCategory={submitCustom(activeCategories, setActiveCategories)}
            onSubmitTool={submitCustom(activeTools, setActiveTools)}
            onSubmitSoftwareTool={submitCustom(activeSoftwareTools, setActiveSoftwareTools)}
            onClear={resetAll}
          />
        </div>
      </section>

      {/* Results header */}
      <div className="mt-14 flex items-baseline justify-between">
        <h2 className="font-display font-semibold text-lg text-[#111827]">
          {filtered.length} {filtered.length === 1 ? "person" : "people"}
        </h2>
        <p className="text-sm text-[#9CA3AF]">Sorted by relevance</p>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div
          data-testid="empty-state"
          className="mt-6 bg-white rounded-3xl ring-1 ring-[#F3F4F6] p-12 flex flex-col items-center text-center"
        >
          <span className="w-16 h-16 rounded-full bg-[#D6FFEB] text-[#3FFFB6] flex items-center justify-center mb-6">
            <SearchX size={28} />
          </span>
          <h3 className="font-display font-semibold text-xl text-[#111827]">
            No one has solved this yet.
          </h3>
          <p className="mt-2 text-[#4B5563] max-w-md">
            Post it as an open problem and interested solvers can reach out
            to you.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setPostOpen(true)}
              data-testid="empty-post-problem"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
            >
              <Plus size={16} />
              Post a problem
            </button>
            <button
              onClick={resetAll}
              data-testid="empty-state-reset"
              className="px-5 py-2.5 rounded-full bg-white ring-1 ring-[#E5E7EB] text-[#111827] text-sm font-medium hover:ring-[#111827] transition-colors"
            >
              Reset filters
            </button>
          </div>
        </div>
      ) : (
        <section
          data-testid="people-grid"
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {filtered.map((p, i) => (
            <PersonCard key={p.id} person={p} index={i} />
          ))}
        </section>
      )}

      <PostProblemModal open={postOpen} onOpenChange={setPostOpen} />
      <AIMatchDialog open={aiMatchOpen} onOpenChange={setAiMatchOpen} />
    </div>
  );
}
